<script setup>
import { useForm } from "@/form";
import { FormGroupListbox, FormInput, FormTextarea } from "@Components/Form";
import FormModal from "@Components/Modal/FormModal.vue";
import { PrimaryButton } from "@Components/Button";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import PostCombobox from "./Partials/PostCombobox.vue";
import Loading from "@Components/Animation/Loading.vue";
import ActionMessage from "@Components/ActionMessage.vue";
import { ArrowPathIcon } from "@heroicons/vue/20/solid";
import { MagicIcon } from "@Icons/index";
import { aiModelsByProvider as imageAIModelsByProvider } from "@Utils/image-models";

const { t: __ } = useI18n();

const modal = ref();
const previewResult = ref();

const [postTypes] = await Promise.all([
    new Promise((resolve, reject) => {
        useForm()
            .post("wordpress_post_types")
            .then((response) => {
                resolve(response);
            })
            .catch((response) => {
                reject();
            });
    }),
]);

const generateImageForm = useForm({
    image_model: "flux_1_dev",
    prompt: "",
});

const generateImageDescriptionForm = useForm({
    title: "",
});

const saveImageForm = useForm({
    name: "",
    description: "",
    image_model: "flux_1_dev",
    path: undefined,
    post_id: null,
});

const submitGenerateImage = async () => {
    if (generateImageForm.processing) {
        return;
    }

    const result = await generateImageForm.post("image_generator_generate");

    if (result.image !== undefined) {
        previewResult.value = result;
    }
};

const generateImageDescription = async (title) => {
    if (generateImageDescriptionForm.processing) {
        return;
    }

    generateImageDescriptionForm.title = title;

    const result = await generateImageDescriptionForm.post("image_generator_generate_description");

    if (result.description !== undefined) {
        saveImageForm.description = result.description;
    }
};

const submitSaveImage = async () => {
    if (!previewResult.value) {
        return;
    }

    if (saveImageForm.processing) {
        return;
    }

    saveImageForm.image_model = previewResult.value.image_model;
    saveImageForm.path = previewResult.value.path;

    await saveImageForm.post("image_generator_save");

    saveImageForm.reset();
};

const updateSelectedPost = (post) => {
    if (post) {
        saveImageForm.post_id = post.ID;
        saveImageForm.name = post.post_title;
        generateImageDescription(post.post_title);
    }
};
</script>

<template>
    <div class="px-4 py-16 lg:px-8 sm:px-6">
        <div class="mx-auto max-w-3xl">
            <h2 class="text-4xl font-semibold leading-tight text-center text-gray-900 dark:text-white">
                {{ __("Générez des images IA") }}
            </h2>
        </div>

        <div class="grid grid-cols-12 gap-6 mt-16">
            <form @submit.prevent="submitGenerateImage" class="col-span-full space-y-6 lg:col-span-5">
                <FormGroupListbox v-model="generateImageForm.image_model" :groups="imageAIModelsByProvider" :label="__('Source des images')" required />

                <FormTextarea
                    :label="__('Saisissez votre prompt')"
                    v-model="generateImageForm.prompt"
                    :errors="generateImageForm.getErrors('prompt')"
                    rows="6" />
                <div class="flex justify-center">
                    <PrimaryButton type="submit" :processing="generateImageForm.processing" :icon="MagicIcon">
                        {{ __("Générer") }}
                    </PrimaryButton>
                </div>
            </form>
            <div class="col-span-full space-y-6 lg:col-span-7">
                <template v-if="previewResult">
                    <img :src="'data:image/png;base64, ' + previewResult.image.base64" />
                    <div class="flex justify-center">
                        <PrimaryButton @click="$refs['modal'].show()">
                            {{ __("Sauvegarder") }}
                        </PrimaryButton>
                    </div>
                </template>
                <template v-else-if="generateImageForm.processing">
                    <div class="w-full h-full flex flex-col justify-center items-center">
                        <Loading class="w-24 h-24" />
                    </div>
                </template>
            </div>
        </div>
        <FormModal ref="modal" @submit="submitSaveImage" :title="__('Sauvegarder l\'image')">
            <template #body>
                <div class="space-y-6">
                    <PostCombobox :modelValue="saveImageForm.post_id" @update:modelValue="updateSelectedPost" :postTypes="postTypes" />
                    <FormInput v-model="saveImageForm.name" :label="__('Titre')" :z="1" />
                    <FormInput
                        v-model="saveImageForm.description"
                        :label="__('Texte alternatif')"
                        :class="[saveImageForm.name.length > 0 ? 'pr-12' : '']"
                        :z="1">
                        <template #action v-if="saveImageForm.name.length > 0">
                            <div class="absolute inset-y-0 right-0 flex items-center mr-4">
                                <svg
                                    v-if="generateImageDescriptionForm.processing"
                                    class="w-4 h-4 text-gray-200 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <ArrowPathIcon
                                    v-else
                                    class="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-200"
                                    @click="generateImageDescription(saveImageForm.name)" />
                            </div>
                        </template>
                    </FormInput>
                </div>
            </template>
            <template #actions>
                <div class="flex justify-end items-center">
                    <ActionMessage :on="saveImageForm.recentlySuccessful" class="mr-3"> {{ __("Sauvegardé.") }} </ActionMessage>
                    <PrimaryButton :processing="saveImageForm.processing" type="submit" :z="1">
                        {{ __("Sauvegarder") }}
                    </PrimaryButton>
                </div>
            </template>
        </FormModal>
    </div>
</template>
